// Pending invitations — EXPLICIT choice only (firm-move lifecycle, 2026-10-05).
//
// Nothing here runs automatically: the user sees each pending invitation
// (firm name + intended role) and chooses ONE. Accepting an invitation from a
// different firm while already in a firm is a firm switch, so it needs a
// second, explicit confirmation; the server then re-checks the user's current
// firm (expectedCurrentFirmId) and refuses a stale or silent move, and refuses
// the last active administrator of a firm leaving it.
import React, { useState } from 'react';
import { acceptInvitation } from '../services/userService';
import { sendVerificationEmail } from '../services/authService';
import { auth } from '../firebase-config';

const roleLabel = (r) => (r ? r.charAt(0).toUpperCase() + r.slice(1) : 'Member');

const box = { background: '#0f1219', border: '1px solid rgba(212,168,83,0.35)', borderRadius: '12px', padding: '1rem 1.25rem', color: '#e8eaed' };
const btn = (primary) => ({
  padding: '0.5rem 1rem', borderRadius: '8px', border: primary ? 'none' : '1px solid rgba(255,255,255,0.15)',
  background: primary ? '#d4a853' : 'transparent', color: primary ? '#0f1219' : '#e8eaed', fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit', fontSize: '0.85rem',
});

// M1 (2026-10-08): an account whose email is not yet verified is told only
// HOW MANY invitations await it — never which firm or role — and is asked to
// verify. App passes this single locked entry through the usual `invites` prop.
export const lockedInvitesPlaceholder = (count) => ({ inviteId: '__verify_email__', locked: true, count });

const LockedInvitations = ({ count, onDismiss }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const sendLink = async () => {
    const r = await sendVerificationEmail();
    setNote(r.success ? "Verification email sent. Open the link in that email, then choose “I've verified”." : 'We could not send the email right now — please try again shortly.');
  };
  const recheck = async () => {
    setBusy(true);
    try {
      if (auth.currentUser) await auth.currentUser.reload();
      if (auth.currentUser && auth.currentUser.emailVerified) { await auth.currentUser.getIdToken(true); window.location.reload(); return; }
      setNote('Your email address is not verified yet — open the link we sent you first.');
    } catch (e) { setNote('We could not check right now — please try again.'); }
    setBusy(false);
  };
  return (
    <div style={box} role="region" aria-label="Pending invitations">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <strong>{count === 1 ? 'You have an invitation' : `You have ${count} invitations`}</strong>
        {onDismiss && <button type="button" style={{ ...btn(false), padding: '0.25rem 0.6rem' }} onClick={onDismiss}>Not now</button>}
      </div>
      <p style={{ color: '#d4a853', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>Verify your email address to see and accept it.</p>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button type="button" style={btn(false)} disabled={busy} onClick={sendLink}>Send verification email</button>
        <button type="button" style={btn(true)} disabled={busy} onClick={recheck}>{busy ? 'Checking…' : "I've verified"}</button>
      </div>
      {note && <p style={{ color: '#8b8fa3', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>{note}</p>}
    </div>
  );
};

const PendingInvitations = ({ invites, currentFirmId = null, currentFirmName = '', displayName = '', onAccepted, onDismiss }) => {
  if (invites && invites.length === 1 && invites[0].locked) return <LockedInvitations count={invites[0].count} onDismiss={onDismiss} />;
  return <InvitationList invites={invites} currentFirmId={currentFirmId} currentFirmName={currentFirmName} displayName={displayName} onAccepted={onAccepted} onDismiss={onDismiss} />;
};

const InvitationList = ({ invites, currentFirmId = null, currentFirmName = '', displayName = '', onAccepted, onDismiss }) => {
  const [confirming, setConfirming] = useState(null); // inviteId awaiting the "switch firms" confirmation
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  // C1 invite-takeover remediation (2026-10-07): the server only redeems an
  // invitation for a VERIFIED email address. Offer the link and a retry
  // instead of a dead end.
  const [verifyFor, setVerifyFor] = useState(null); // invite awaiting email verification
  const [verifyNote, setVerifyNote] = useState('');

  if (!invites || invites.length === 0) return null;

  const accept = async (invite) => {
    const isSwitch = !!currentFirmId && invite.firmId !== currentFirmId;
    if (isSwitch && confirming !== invite.inviteId) { setConfirming(invite.inviteId); setError(''); return; }
    setBusy(invite.inviteId); setError('');
    const r = await acceptInvitation(invite.inviteId, currentFirmId || null, displayName);
    setBusy(null);
    if (!r.success) {
      if (r.reason === 'EMAIL_NOT_VERIFIED') { setVerifyFor(invite); setVerifyNote(''); setError(''); return; }
      setError(r.error || 'The invitation could not be accepted.'); setConfirming(null); return;
    }
    setVerifyFor(null);
    if (onAccepted) onAccepted(r, invite);
  };

  const sendLink = async () => {
    const r = await sendVerificationEmail();
    setVerifyNote(r.success ? "Verification email sent. Open the link in that email, then choose “I've verified — join”." : 'We could not send the email right now — please try again.');
  };

  const retryAfterVerify = async () => {
    try { if (auth.currentUser) { await auth.currentUser.reload(); await auth.currentUser.getIdToken(true); } } catch (e) {}
    if (verifyFor) await accept(verifyFor);
  };

  return (
    <div style={box} role="region" aria-label="Pending invitations">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <strong>{invites.length === 1 ? 'You have an invitation' : `You have ${invites.length} invitations`}</strong>
        {onDismiss && <button type="button" style={{ ...btn(false), padding: '0.25rem 0.6rem' }} onClick={onDismiss}>Not now</button>}
      </div>
      {invites.map((inv) => {
        const isSwitch = !!currentFirmId && inv.firmId !== currentFirmId;
        const sameFirm = !!currentFirmId && inv.firmId === currentFirmId;
        return (
          <div key={inv.inviteId} style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '0.75rem 0' }}>
            <div style={{ fontSize: '0.9rem' }}>
              <strong>{inv.firmName || 'A firm'}</strong> invited you to join as <strong>{roleLabel(inv.role)}</strong>.
            </div>
            {confirming === inv.inviteId && isSwitch && (
              <p style={{ color: '#f59e0b', fontSize: '0.82rem', margin: '0.5rem 0' }}>
                Accepting will remove you from {currentFirmName || 'your current firm'} — you will lose access to its matters, clients and documents —
                and make you a member of {inv.firmName || 'the inviting firm'}. Confirm to switch firms.
              </p>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
              <button type="button" style={btn(true)} disabled={!!busy} onClick={() => accept(inv)}>
                {busy === inv.inviteId ? 'Working…' : confirming === inv.inviteId && isSwitch ? 'Confirm: switch firms' : sameFirm ? 'Confirm invitation' : isSwitch ? 'Accept and switch firms' : 'Accept invitation'}
              </button>
              {confirming === inv.inviteId && <button type="button" style={btn(false)} disabled={!!busy} onClick={() => setConfirming(null)}>Cancel</button>}
            </div>
          </div>
        );
      })}
      {verifyFor && (
        <div role="status" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '0.75rem', marginTop: '0.25rem' }}>
          <p style={{ color: '#d4a853', fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
            Please verify your email address before joining {verifyFor.firmName || 'this firm'}.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button type="button" style={btn(false)} disabled={!!busy} onClick={sendLink}>Send verification email</button>
            <button type="button" style={btn(true)} disabled={!!busy} onClick={retryAfterVerify}>{busy ? 'Working…' : "I've verified — join"}</button>
          </div>
          {verifyNote && <p style={{ color: '#8b8fa3', fontSize: '0.8rem', margin: '0.5rem 0 0' }}>{verifyNote}</p>}
        </div>
      )}
      {error && <p role="alert" style={{ color: '#ef4444', fontSize: '0.82rem', margin: '0.5rem 0 0' }}>{error}</p>}
    </div>
  );
};

export default PendingInvitations;
